import errorImage from "@assets/errors/error-screen.svg";
import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import CopyButton from "@components/buttons/CopyButton";
import TransactionStatusClass from "@components/classes/TransactionStatusClass";
import GlobalSearchInputField from "@components/inputs/GlobalSearchInputField";
import DataPill from "@components/pills/DataPill";
import Switcher from "@components/switcher/Switcher";
import { userManagementApis } from "@services/apis/UserManagement";
import {
  UsersRequest,
  UsersResponseData,
} from "@services/models/UserManagement/users";
import { ColDef, RowClickedEvent } from "ag-grid-community";
import { useEffect, useMemo, useRef, useState } from "react";
import { Outlet } from "react-router-dom";
import ConstantFunctions from "../../utils/constants_functions";
import Account from "./Account";
import Kyckyb from "./KycKyb";
import Services from "./Services";
import Wallet from "./Wallet";

const ROWS_PER_PAGE = 100;

const UserManagement = () => {
  const transactionStatusClass = new TransactionStatusClass();
  const constantFunction = new ConstantFunctions();
  const individualGridRef = useRef<any>(null);
  const merchantGridRef = useRef<any>(null);
  const [error, setError] = useState("");
  const [individualSearchTerm, setIndividualSearchTerm] = useState("");
  const [appliedIndividualSearch, setAppliedIndividualSearch] = useState("");
  const [merchantSearchTerm, setMerchantSearchTerm] = useState("");
  const [appliedMerchantSearch, setAppliedMerchantSearch] = useState("");
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [, setIsIndividual] = useState<boolean | null>(null);
  const [isPanelCollapsed, setIsPanelCollapsed] = useState<boolean>(false);

  const individualFetchingRef = useRef(false);
  const merchantFetchingRef = useRef(false);
  const individualRequestIdRef = useRef(0);
  const merchantRequestIdRef = useRef(0);
  const individualPageRef = useRef(1);
  const merchantPageRef = useRef(1);
  const individualHasMoreRef = useRef(true);
  const merchantHasMoreRef = useRef(true);
  const appliedIndividualSearchRef = useRef("");
  const appliedMerchantSearchRef = useRef("");
  const didInitialFetch = useRef(false);

  // Individual
  const [individualUsers, setIndividualUsers] = useState<UsersResponseData[]>(
    [],
  );
  const [individualLoading, setIndividualLoading] = useState(false);

  // Merchant pagination
  const [merchantUsers, setMerchantUsers] = useState<UsersResponseData[]>([]);
  const [merchantLoading, setMerchantLoading] = useState(false);

  const [isUserProfileMissing, setIsUserProfileMissing] = useState(false);

  // Get the selected user's account type
  const getSelectedUserAccountType = (): string => {
    if (!selectedUserId) return "";

    const individualUser = individualUsers.find(
      (user) => user.userId === selectedUserId,
    );
    if (individualUser) return individualUser.accountType;

    const merchantUser = merchantUsers.find(
      (user) => user.userId === selectedUserId,
    );
    if (merchantUser) return merchantUser.accountType;

    return "";
  };

  const fetchUserProfile = async ({
    userId,
    accountType,
  }: {
    userId: number;
    accountType: string;
  }) => {
    try {
      setIsUserProfileMissing(false);

      let type =
        accountType === "institutional" || accountType === "small_merchant"
          ? "merchant"
          : "individual";

      await userManagementApis.getUserDetails(userId, type);
    } catch (error: any) {
      const message = error?.response?.data?.msg;

      if (error?.response?.status === 404 && message === "No user found") {
        setIsUserProfileMissing(true);
        return;
      }

      setError("Failed to fetch user profile");
    }
  };

  const tabs = [
    {
      label: "ACCOUNT",
      component: (
        <Account
          userId={selectedUserId!}
          accountType={getSelectedUserAccountType()}
        />
      ),
    },
    {
      label: "SERVICES",
      component: (
        <Services
          userId={selectedUserId!}
          accountType={getSelectedUserAccountType()}
        />
      ),
    },
    {
      label: "WALLET",
      component: (
        <Wallet
          userId={selectedUserId!}
          accountType={getSelectedUserAccountType()}
        />
      ),
    },
    {
      label: getSelectedUserAccountType() === "institutional" ? "KYB" : "KYC",
      component: (
        <Kyckyb
          userId={selectedUserId!}
          accountType={getSelectedUserAccountType()}
        />
      ),
    },
  ];

  const selectFirstIndividualUser = (users: UsersResponseData[]) => {
    if (users.length === 0) return;

    const firstUser = users[0];
    setSelectedUserId(firstUser.userId);
    setIsIndividual(true);
    setIsPanelCollapsed(false);

    setTimeout(() => {
      const gridApi = individualGridRef.current?.api;
      if (gridApi) {
        const firstNode = gridApi.getDisplayedRowAtIndex(0);
        if (firstNode) {
          firstNode.setSelected(true);

          const rowElement = document.querySelector(
            `[row-id="${firstNode.id}"]`,
          );

          if (rowElement) {
            document
              .querySelectorAll(".ag-row")
              .forEach((r) => r.classList.remove("ag-row-selected-custom"));

            rowElement.classList.add("ag-row-selected-custom");
          }
        }
      }
    }, 50);
  };

  const fetchIndividualUsers = async (
    pageNumber: number = 1,
    reset: boolean = false,
    search: string = appliedIndividualSearch,
  ) => {
    const requestId = reset
      ? ++individualRequestIdRef.current
      : individualRequestIdRef.current;

    individualFetchingRef.current = true;
    setIndividualLoading(true);
    setError("");
    if (reset) {
      setIndividualUsers([]);
    }

    try {
      const request: UsersRequest = {
        page: pageNumber,
        limit: ROWS_PER_PAGE,
        type: "individual",
        ...(search ? { search } : {}),
      };

      const response = await userManagementApis.getUsers(request);
      if (requestId !== individualRequestIdRef.current) return;

      const newData = response.data || [];

      if (reset) {
        setIndividualUsers(newData);
      } else {
        setIndividualUsers((prev) => [...prev, ...newData]);
      }

      individualHasMoreRef.current = newData.length >= ROWS_PER_PAGE;

      if (reset) {
        selectFirstIndividualUser(newData);
      }
    } catch (error) {
      if (requestId !== individualRequestIdRef.current) return;
      setError("Failed to fetch users.");
    } finally {
      if (requestId === individualRequestIdRef.current) {
        setIndividualLoading(false);
        individualFetchingRef.current = false;
      }
    }
  };

  const fetchMerchantUsers = async (
    pageNumber: number = 1,
    reset: boolean = false,
    search: string = appliedMerchantSearch,
  ) => {
    const requestId = reset
      ? ++merchantRequestIdRef.current
      : merchantRequestIdRef.current;

    merchantFetchingRef.current = true;
    setMerchantLoading(true);
    setError("");
    if (reset) {
      setMerchantUsers([]);
    }

    try {
      const request: UsersRequest = {
        page: pageNumber,
        limit: ROWS_PER_PAGE,
        type: "merchant",
        ...(search ? { search } : {}),
      };

      const response = await userManagementApis.getUsers(request);
      if (requestId !== merchantRequestIdRef.current) return;

      const newData = response.data || [];

      if (reset) {
        setMerchantUsers(newData);
      } else {
        setMerchantUsers((prev) => [...prev, ...newData]);
      }

      merchantHasMoreRef.current = newData.length >= ROWS_PER_PAGE;
    } catch (error) {
      if (requestId !== merchantRequestIdRef.current) return;
      setError("Failed to fetch users.");
    } finally {
      if (requestId === merchantRequestIdRef.current) {
        setMerchantLoading(false);
        merchantFetchingRef.current = false;
      }
    }
  };

  const handleTabChange = (index: number) => {
    setActiveTabIndex(index);
  };

  const individualColumnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "UID",
        field: "userId",
        minWidth: 70,
        flex: 1,
        cellRenderer: ({ data }: any) => (
          <div className="flex items-center justify-between space-x-[10px]">
            <span>{data.userId}</span>
            <CopyButton
              copyText={data.userId}
              copyMessage="User ID copied!"
              buttonSize="14px"
              copyIconSize={12}
            />
          </div>
        ),
      },
      {
        headerName: "Email ID",
        field: "emailId",
        minWidth: 140,
        flex: 2,

        cellRenderer: ({ data }: any) => {
          const value = data?.emailId ? data.emailId.toString() : "";

          return (
            <div className="flex items-center h-full w-full gap-2">
              <div className="flex flex-col leading-[14px] flex-1 min-w-0">
                {value ? (
                  Array.from(
                    { length: Math.ceil(value.length / 14) },
                    (_, index) => (
                      <span
                        key={index}
                        className="text-[10px] text-text-base break-all"
                        title={value}
                      >
                        {value.slice(index * 14, (index + 1) * 14)}
                      </span>
                    ),
                  )
                ) : (
                  <span className="text-[10px] text-text-base">- -</span>
                )}
              </div>

              {value && (
                <div className="flex-shrink-0">
                  <CopyButton
                    copyText={value}
                    copyMessage="Email ID copied!"
                    buttonSize="14px"
                    copyIconSize={12}
                  />
                </div>
              )}
            </div>
          );
        },
      },
      {
        headerName: "Category",
        field: "accountType",
        minWidth: 100,
        flex: 1,
        sortable: true,
        cellRenderer: ({ data }: any) => (
          <DataPill value={data?.accountType} />
        ),
      },
      {
        headerName: "KYC Status",
        minWidth: 100,
        flex: 1,
        sortable: true,
        cellRenderer: ({ data }: any) => {
          const bgColor =
            data.overallKycStatus &&
            transactionStatusClass.getTransactionBgColor(data.overallKycStatus);

          const textColor =
            data.overallKycStatus &&
            transactionStatusClass.getTransactionIconColor(
              data.overallKycStatus,
            );

          return (
            <span
              className={`inline-flex h-[20px] items-center justify-center gap-1 whitespace-nowrap rounded px-[6px] text-[10px] font-medium ${bgColor}`}
              style={{ color: textColor }}
            >
              {data.overallKycStatus?.toUpperCase() || "-"}
            </span>
          );
        },
      },
      {
        headerName: "USDT",
        field: "usdt_balance",
        minWidth: 90,
        flex: 1,
        sortable: true,
        cellRenderer: ({ data }: any) => (
          <div className="flex items-center justify-between space-x-[10px]">
            <img
              src={"https://assets.tylt.money/holdings-icons/USDT.png"}
              alt={"USDT"}
              className="w-[12px] h-[12px] rounded"
            />
            <span>{constantFunction.formatAmount(data.usdt_balance)}</span>
          </div>
        ),
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "USDC",
        minWidth: 90,
        flex: 1,
        sortable: true,
        cellRenderer: ({ data }: any) => (
          <div className="flex items-center justify-between space-x-[10px]">
            <img
              src={"https://assets.tylt.money/holdings-icons/USDC.png"}
              alt={"USDC"}
              className="w-[12px] h-[12px] rounded"
            />
            <span>{constantFunction.formatAmount(data.usdc_balance)}</span>
          </div>
        ),
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "DAI",
        field: "dai_balance",
        minWidth: 90,
        flex: 1,
        sortable: true,
        cellRenderer: ({ data }: any) => (
          <div className="flex items-center justify-between space-x-[10px]">
            <img
              src={"https://assets.tylt.money/holdings-icons/DAI.png"}
              alt={"DAI"}
              className="w-[12px] h-[12px] rounded"
            />
            <span>{constantFunction.formatAmount(data.dai_balance)}</span>
          </div>
        ),
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Total",
        field: "total_balance",
        minWidth: 100,
        flex: 1,
        sortable: true,
        cellRenderer: ({ data }: any) => (
          <div className="flex items-center justify-between space-x-[10px]">
            <span>${constantFunction.formatAmount(data.total_balance)}</span>
          </div>
        ),
        filter: "agNumberColumnFilter",
      },
    ],
    [],
  );

  const merchantColumnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "UID",
        field: "userId",
        minWidth: 70,
        flex: 1,
        cellRenderer: ({ data }: any) => (
          <div className="flex items-center justify-between space-x-[10px]">
            <span>{data.userId}</span>
            <CopyButton
              copyText={data.userId}
              copyMessage="User ID copied!"
              buttonSize="14px"
              copyIconSize={12}
            />
          </div>
        ),
      },
      {
        headerName: "Email ID",
        field: "emailId",
        minWidth: 140,
        flex: 2,

        cellRenderer: ({ data }: any) => {
          const value = data?.emailId ? data.emailId.toString() : "";

          return (
            <div className="flex items-center h-full w-full gap-2">
              <div className="flex flex-col leading-[14px] flex-1 min-w-0">
                {value ? (
                  Array.from(
                    { length: Math.ceil(value.length / 14) },
                    (_, index) => (
                      <span
                        key={index}
                        className="text-[10px] text-text-base break-all"
                        title={value}
                      >
                        {value.slice(index * 14, (index + 1) * 14)}
                      </span>
                    ),
                  )
                ) : (
                  <span className="text-[10px] text-text-base">- -</span>
                )}
              </div>

              {value && (
                <div className="flex-shrink-0">
                  <CopyButton
                    copyText={value}
                    copyMessage="Email ID copied!"
                    buttonSize="14px"
                    copyIconSize={12}
                  />
                </div>
              )}
            </div>
          );
        },
      },
      {
        headerName: "Category",
        field: "accountType",
        minWidth: 130,
        flex: 1,
        sortable: true,
        cellRenderer: ({ data }: any) => (
          <DataPill value={data?.accountType} />
        ),
      },
      {
        headerName: "Submitted",
        minWidth: 80,
        flex: 1,
        sortable: true,
        cellClass: "flex items-center justify-center", // ⭐ this centers content

        cellRenderer: ({ data }: any) => {
          const kybStatus = data.isSubmitted ? "YES" : "NO";

          const bgColor = transactionStatusClass.getTransactionBgColor(
            kybStatus.toLowerCase(),
          );

          const textColor = transactionStatusClass.getTransactionIconColor(
            kybStatus.toLowerCase(),
          );

          return (
            <span
              className={`h-[20px] flex items-center justify-center rounded text-[10px] font-medium px-[10px] ${bgColor}`}
              style={{ color: textColor }}
            >
              {kybStatus}
            </span>
          );
        },
      },
      {
        headerName: "USDT",
        field: "usdt_balance",
        minWidth: 90,
        flex: 1,
        sortable: true,
        cellRenderer: ({ data }: any) => (
          <div className="flex items-center justify-between space-x-[10px]">
            <img
              src={"https://assets.tylt.money/holdings-icons/USDT.png"}
              alt={"USDT"}
              className="w-[12px] h-[12px] rounded"
            />
            <span>{constantFunction.formatAmount(data.usdt_balance)}</span>
          </div>
        ),
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "USDC",
        minWidth: 90,
        flex: 1,
        sortable: true,
        cellRenderer: ({ data }: any) => (
          <div className="flex items-center justify-between space-x-[10px]">
            <img
              src={"https://assets.tylt.money/holdings-icons/USDC.png"}
              alt={"USDC"}
              className="w-[12px] h-[12px] rounded"
            />
            <span>{constantFunction.formatAmount(data.usdc_balance)}</span>
          </div>
        ),
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "DAI",
        field: "dai_balance",
        minWidth: 90,
        flex: 1,
        sortable: true,
        cellRenderer: ({ data }: any) => (
          <div className="flex items-center justify-between space-x-[10px]">
            <img
              src={"https://assets.tylt.money/holdings-icons/DAI.png"}
              alt={"DAI"}
              className="w-[12px] h-[12px] rounded"
            />
            <span>{constantFunction.formatAmount(data.dai_balance)}</span>
          </div>
        ),
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Total",
        field: "total_balance",
        minWidth: 100,
        flex: 1,
        sortable: true,
        cellRenderer: ({ data }: any) => (
          <div className="flex items-center justify-between space-x-[10px]">
            <span>${constantFunction.formatAmount(data.total_balance)}</span>
          </div>
        ),
        filter: "agNumberColumnFilter",
      },
    ],
    [],
  );

  const onRowClicked = (event: RowClickedEvent, isIndividual: boolean) => {
    // Clear all selected rows from both grids
    const allRows = document.querySelectorAll(".ag-row");
    allRows.forEach((row) => {
      row.classList.remove("ag-row-selected-custom");
    });

    // Use the event's api to get the grid element directly
    if (event.rowIndex !== null) {
      // The event.api belongs to the specific grid that was clicked
      const gridElement =
        (event.api as any).gridCore?.eGridDiv || (event.api as any).eGridDiv;

      if (gridElement) {
        const rowElement = gridElement.querySelector(
          `[row-index="${event.rowIndex}"]`,
        );
        if (rowElement) {
          rowElement.classList.add("ag-row-selected-custom");
        }
      } else {
        // Fallback: Find the row element through the clicked row node
        const rowNode = event.node;
        if (rowNode) {
          const rowElement = document.querySelector(`[row-id="${rowNode.id}"]`);
          if (rowElement) {
            rowElement.classList.add("ag-row-selected-custom");
          }
        }
      }
    }
    setSelectedUserId(event.data.userId);
    setIsIndividual(isIndividual);
    setIsPanelCollapsed(false);
    setIsUserProfileMissing(false);

    fetchUserProfile({
      userId: event.data.userId,
      accountType: event.data.accountType,
    });
  };

  const handleIndividualScrollEnd = (params: any) => {
    if (!individualHasMoreRef.current || individualFetchingRef.current) {
      return;
    }

    const api = params.api;
    const displayedRowCount = api.getDisplayedRowCount();
    const lastDisplayedRowIndex = api.getLastDisplayedRowIndex();

    if (
      lastDisplayedRowIndex >= displayedRowCount - 5 &&
      displayedRowCount > 0
    ) {
      individualFetchingRef.current = true;
      individualPageRef.current += 1;
      fetchIndividualUsers(
        individualPageRef.current,
        false,
        appliedIndividualSearchRef.current,
      );
    }
  };

  const handleMerchantScrollEnd = (params: any) => {
    if (!merchantHasMoreRef.current || merchantFetchingRef.current) {
      return;
    }

    const api = params.api;
    const displayedRowCount = api.getDisplayedRowCount();
    const lastDisplayedRowIndex = api.getLastDisplayedRowIndex();

    if (
      lastDisplayedRowIndex >= displayedRowCount - 5 &&
      displayedRowCount > 0
    ) {
      merchantFetchingRef.current = true;
      merchantPageRef.current += 1;
      fetchMerchantUsers(
        merchantPageRef.current,
        false,
        appliedMerchantSearchRef.current,
      );
    }
  };

  const handleIndividualSearchSubmit = () => {
    const value = individualSearchTerm.trim();
    if (!value) return;

    setSelectedUserId(null);
    appliedIndividualSearchRef.current = value;
    individualPageRef.current = 1;
    individualHasMoreRef.current = true;
    setAppliedIndividualSearch(value);
    fetchIndividualUsers(1, true, value);
  };

  const clearIndividualSearch = () => {
    setIndividualSearchTerm("");
    appliedIndividualSearchRef.current = "";
    individualPageRef.current = 1;
    individualHasMoreRef.current = true;
    setAppliedIndividualSearch("");
    setSelectedUserId(null);
    fetchIndividualUsers(1, true, "");
  };

  const handleMerchantSearchSubmit = () => {
    const value = merchantSearchTerm.trim();
    if (!value) return;

    appliedMerchantSearchRef.current = value;
    merchantPageRef.current = 1;
    merchantHasMoreRef.current = true;
    setAppliedMerchantSearch(value);
    fetchMerchantUsers(1, true, value);
  };

  const clearMerchantSearch = () => {
    setMerchantSearchTerm("");
    appliedMerchantSearchRef.current = "";
    merchantPageRef.current = 1;
    merchantHasMoreRef.current = true;
    setAppliedMerchantSearch("");
    fetchMerchantUsers(1, true, "");
  };

  useEffect(() => {
    if (didInitialFetch.current) return;
    didInitialFetch.current = true;
    fetchIndividualUsers(1, true, "");
    fetchMerchantUsers(1, true, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isInitialIndividualLoading =
    individualLoading && individualUsers.length === 0;
  const isInitialMerchantLoading =
    merchantLoading && merchantUsers.length === 0;

  return (
    <div className="h-[100dvh] flex flex-col overflow-hidden">
      <div className="min-h-[70px] border-b border-border-subtle bg-bg-page-body flex flex-row sticky top-0 bg-white z-10 pl-[20px] w-full">
        <div className="flex flex-grow justify-between items-center w-full mr-4">
          <p className="flex text-text-base text-[14px] font-semibold items-center">
            Users
          </p>
        </div>
      </div>
      <div className="flex flex-row gap-0 items-start h-full overflow-hidden">
        {/* Left Content */}
        <div className="flex flex-col flex-[0_0_60%] h-[100%] px-[16px] pt-[16px] overflow-hidden">
          <div className="flex flex-row justify-between items-center mb-[8px]">
            <span className="text-text-base font-semibold text-[12px] mb-[8px]">
              Individual
            </span>
            <div className="w-[360px] flex-shrink-0">
              <GlobalSearchInputField
                value={individualSearchTerm}
                onChange={(e) => setIndividualSearchTerm(e.target.value)}
                onSearch={handleIndividualSearchSubmit}
                onClear={clearIndividualSearch}
                disableSearchButton={!individualSearchTerm.trim()}
                isSearchApplied={Boolean(appliedIndividualSearch)}
                placeholder="Search by User ID / Email ID"
              />
            </div>
          </div>

          <div className="flex-1 bg-bg-page-body overflow-hidden">
            {isInitialIndividualLoading ? (
              <AgGridSkeleton
                columnDefs={individualColumnDefs}
                style={{ height: "100%", width: "100%" }}
              />
            ) : (
              <div className="flex flex-col h-full">
                <div className="ag-theme-alpine ag-grid-12px flex-1 min-h-0 w-full">
                  <AgGridReactTable
                    ref={individualGridRef}
                    onRowClicked={(event) => onRowClicked(event, true)}
                    onBodyScroll={handleIndividualScrollEnd}
                    rowData={individualUsers}
                    columnDefs={individualColumnDefs}
                    error={error}
                    getRowId={(params) => params.data.userId.toString()}
                  />
                </div>
                {individualLoading && individualUsers.length > 0 && (
                  <div className="flex items-center justify-center py-2 border-t border-border-subtle">
                    <div className="flex items-center space-x-2 text-text-subtle">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-border-subtle border-t-border-button-primary"></div>
                      <span className="text-sm">Loading more users...</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-row justify-between items-center my-[8px]">
            <span className="text-text-base font-semibold text-[12px]">
              Merchant
            </span>
            <div className="w-[360px] flex-shrink-0">
              <GlobalSearchInputField
                value={merchantSearchTerm}
                onChange={(e) => setMerchantSearchTerm(e.target.value)}
                onSearch={handleMerchantSearchSubmit}
                onClear={clearMerchantSearch}
                disableSearchButton={!merchantSearchTerm.trim()}
                isSearchApplied={Boolean(appliedMerchantSearch)}
                placeholder="Search by User ID / Email ID"
              />
            </div>
          </div>

          <div className="flex-1 bg-bg-page-body overflow-hidden">
            {isInitialMerchantLoading ? (
              <AgGridSkeleton
                columnDefs={merchantColumnDefs}
                style={{ height: "100%", width: "100%" }}
              />
            ) : (
              <div className="flex flex-col h-full">
                <div className="flex-1 min-h-0">
                  <AgGridReactTable
                    ref={merchantGridRef}
                    rowData={merchantUsers}
                    onRowClicked={(event) => onRowClicked(event, false)}
                    onBodyScroll={handleMerchantScrollEnd}
                    columnDefs={merchantColumnDefs}
                    error={error}
                    getRowId={(params) => params.data.userId.toString()}
                  />
                </div>
                {merchantLoading && merchantUsers.length > 0 && (
                  <div className="flex items-center justify-center py-2 border-t border-border-subtle">
                    <div className="flex items-center space-x-2 text-text-subtle">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-border-subtle border-t-border-button-primary"></div>
                      <span className="text-sm">Loading more users...</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {/* Right Content */}
        {selectedUserId && !isPanelCollapsed && (
          <div className="flex-[0_0_40%] h-full overflow-hidden">
            <div className="h-full p-[16px]">
              {isUserProfileMissing ? (
                <div className="flex flex-col items-center justify-center rounded-[12px] bg-bg-page-body">
                  <img
                    src={errorImage}
                    alt="Error Illustration"
                    className="w-[300px] h-auto max-h-[300px] object-contain "
                  />
                  <p className="text-[14px] font-medium text-text-base mt-[16px]">
                    User profile doesn't exist!!
                  </p>
                </div>
              ) : (
                <Switcher
                  tabs={tabs}
                  activeTabIndex={activeTabIndex}
                  onTabChange={handleTabChange}
                />
              )}
            </div>
          </div>
        )}
      </div>
      <Outlet />
    </div>
  );
};

export default UserManagement;
